import { useEffect, useState } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../i18n';
import { fetchAllVideos, findVideoIdBySlug, type VideoMeta } from '../youtube';
import { slugify } from '../slug';

interface LocationState { videoId?: string }

export default function VideoDetail() {
  const { videoSlug = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [video, setVideo] = useState<VideoMeta | null>(null);
  const [resolvedId, setResolvedId] = useState<string | null>(
    (location.state as LocationState)?.videoId ?? null,
  );
  const [error, setError] = useState<string | null>(null);

  // Resolve metadata + canonical slug. Try the cached video list first
  // (rich data, no extra API hit); fall back to a search query if the user
  // landed on the page cold.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all = await fetchAllVideos();
        if (cancelled) return;
        const match = all.find((v) => slugify(v.title) === videoSlug);
        if (match) {
          setVideo(match);
          setResolvedId(match.id);
          const canonical = slugify(match.title);
          if (canonical !== videoSlug) {
            navigate(`/video/${canonical}`, { replace: true, state: { videoId: match.id } });
          }
          return;
        }
        if (!resolvedId) {
          const id = await findVideoIdBySlug(videoSlug);
          if (!cancelled) setResolvedId(id);
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [videoSlug, navigate, resolvedId]);

  if (error) {
    return (
      <div className="error">
        <h2>{t('error')}</h2>
        <p>{error}</p>
        <Link to="/" className="back-link">{t('backToVideos')}</Link>
      </div>
    );
  }

  if (!resolvedId) {
    return <p>{t('loading')}</p>;
  }

  const title = video?.title ?? videoSlug.replace(/-/g, ' ');
  const embedUrl = `https://www.youtube.com/embed/${resolvedId}`;
  const watchUrl = `https://www.youtube.com/watch?v=${resolvedId}`;

  return (
    <article className="video-page">
      <Helmet>
        <title>{title} — RetroGameUp</title>
        <meta property="og:title" content={title} />
        <meta property="og:type" content="video.other" />
        <meta property="og:url" content={watchUrl} />
        {video?.thumbnails && (
          <meta property="og:image" content={(video.thumbnails.maxres ?? video.thumbnails.high)?.url} />
        )}
      </Helmet>

      <Link to="/" className="back-link">{t('backToVideos')}</Link>

      <h1>{title}</h1>

      <div className="video-embed">
        <iframe
          src={embedUrl}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    </article>
  );
}
